// shared/tray – the menu bar quick menu of the desktop programs (AD, docs rimocompta/docs/AD-AE-MENUBAR.md §3/§5).
// 1:1 port of the user's Claude Design „Tray Menu“ (export „Menu bar quick menu design“, 02.11.2026; source kept in
// rimocompta/frontend/design-source/Tray Menu.dc.html): markup, texts DE/FR, colours and the keyboard logic as in the
// design; the design system's Avatar and Switch are rebuilt inline exactly as in its bundle. Rendered in each program's
// own frameless `tray` window (Rust part: README). Owner RimoCompta; changes only here, then tell the supervisor.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import './tray-menu.css';

export type TrayApp = 'compta' | 'chantiers' | 'docs';
export type TrayLang = 'de' | 'fr';
export interface TraySuiteEntry {
  /** Program key as in the design („compta“, „chantiers“, „docs“, „id“, „portail“, „rapports“ …). */
  key: string;
  name: string;
  /** Favicon (hexagon) URL from brand/. */
  src: string;
  /** Runs on this device (presence, contract AE). */
  running?: boolean;
}
export interface TrayMenuProps {
  app: TrayApp;
  lang?: TrayLang;
  platform?: 'mac' | 'win';
  online?: boolean;
  /** The signed-in person. */
  user: { name: string; initials: string; color?: string };
  /** Server shown in „Verbunden · …“. */
  host?: string;
  /** The program's mark (mark-dunkel from brand/). */
  mark: string;
  suite: TraySuiteEntry[];
  /** RimoCompta: Pay-Assistant on/off (the „Bezahlung“ switch). */
  payOn?: boolean;
  onPayChange?: (on: boolean) => void;
  /** Entries that cannot be used now: action id → tooltip, already translated (e.g. RimoDocs' „Scanner“ while RimoPrint is paused). */
  disabled?: Record<string, string>;
  /** The global shortcut is on (shown in the foot). */
  globalShortcut?: boolean;
  /** An action row (its id, see TRAY_ACTIONS), a suite program, „Fenster zeigen“, „Beenden“. */
  onAction: (id: string) => void;
  onSuite: (key: string) => void;
  onShowWindow: () => void;
  onQuit: () => void;
}

const SVG = (els: Array<[string, Record<string, unknown>]>) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    {els.map(([tag, attrs], i) => { const T = tag as 'path'; return <T key={i} {...attrs} />; })}
  </svg>
);
const P = (d: string): [string, Record<string, unknown>] => ['path', { d }];
const ICONS: Record<string, Array<[string, Record<string, unknown>]>> = {
  upload: [P('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'), P('M17 8l-5-5-5 5'), P('M12 3v12')],
  landmark: [P('M3 22h18'), P('M6 18v-7'), P('M10 18v-7'), P('M14 18v-7'), P('M18 18v-7'), ['polygon', { points: '12 2 20 7 4 7' }]],
  dashboard: [['rect', { x: 3, y: 3, width: 7, height: 9, rx: 1 }], ['rect', { x: 14, y: 3, width: 7, height: 5, rx: 1 }], ['rect', { x: 14, y: 12, width: 7, height: 9, rx: 1 }], ['rect', { x: 3, y: 16, width: 7, height: 5, rx: 1 }]],
  card: [['rect', { x: 2, y: 5, width: 20, height: 14, rx: 2 }], P('M2 10h20')],
  filePlus: [P('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'), P('M14 2v6h6'), P('M12 18v-6'), P('M9 15h6')],
  search: [['circle', { cx: 11, cy: 11, r: 8 }], P('m21 21-4.3-4.3')],
  folder: [P('m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2')],
  pen: [P('M12 20h9'), P('M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z')],
  archive: [['rect', { x: 2, y: 3, width: 20, height: 5, rx: 1 }], P('M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8'), P('M10 12h4')],
  scan: [P('M3 7V5a2 2 0 0 1 2-2h2'), P('M17 3h2a2 2 0 0 1 2 2v2'), P('M21 17v2a2 2 0 0 1-2 2h-2'), P('M7 21H5a2 2 0 0 1-2-2v-2'), P('M7 12h10')],
};
const T = {
  de: { signedIn: 'Angemeldet als', online: 'Verbunden · ', offline: 'Offline – Server nicht erreichbar', offlineHint: 'Die Aktionen sind wieder verfügbar, sobald der Server erreichbar ist.', programs: 'Programme', runningHere: 'läuft auf diesem Gerät', showWindow: 'Fenster zeigen', quit: 'Beenden', globalKey: 'Dieses Menü öffnen' },
  fr: { signedIn: 'Connecté en tant que', online: 'Connecté · ', offline: 'Hors ligne – serveur injoignable', offlineHint: 'Les actions seront disponibles dès que le serveur sera joignable.', programs: 'Programmes', runningHere: 'ouvert sur cet appareil', showWindow: 'Afficher la fenêtre', quit: 'Quitter', globalKey: 'Ouvrir ce menu' },
};
/** The quick actions per program as in the design; `id` is what `onAction` receives (the program maps it). */
export const TRAY_ACTIONS: Record<TrayApp, { name: string; letter: string; actions: Array<{ id: string; de: string; fr: string; key?: string; icon: string; sw?: boolean }> }> = {
  compta: { name: 'RimoCompta', letter: 'C', actions: [
    { id: 'import', de: 'Neuer Import', fr: 'Nouvel import', key: 'N', icon: 'upload' },
    { id: 'statement', de: 'Kontoauszug importieren', fr: 'Importer un extrait', key: 'I', icon: 'landmark' },
    { id: 'dashboard', de: 'Dashboard', fr: 'Tableau de bord', key: 'D', icon: 'dashboard' },
    { id: 'pay', de: 'Bezahlung', fr: 'Paiement', sw: true, icon: 'card' }] },
  chantiers: { name: 'RimoChantiers', letter: 'H', actions: [
    { id: 'devis', de: 'Neuer Devis', fr: 'Nouveau devis', key: 'N', icon: 'filePlus' },
    { id: 'search', de: 'Chantier suchen …', fr: 'Rechercher un chantier …', key: 'F', icon: 'search' },
    { id: 'dossier', de: 'Dossier öffnen …', fr: 'Ouvrir un dossier …', key: 'O', icon: 'folder' }] },
  docs: { name: 'RimoDocs', letter: 'D', actions: [
    { id: 'letter', de: 'Neuer Brief', fr: 'Nouvelle lettre', key: 'N', icon: 'pen' },
    { id: 'archive', de: 'Archiv durchsuchen …', fr: 'Rechercher dans l’archive …', key: 'F', icon: 'archive' },
    { id: 'scanner', de: 'Scanner', fr: 'Scanner', key: 'E', icon: 'scan' }] },
};
/** „⌃⌥C“ / „Strg+Alt+C“ – the global shortcut of a program (user decision 01.11.2026). */
export const trayGlobalKey = (app: TrayApp, platform: 'mac' | 'win', lang: TrayLang = 'de') =>
  (platform === 'win' ? (lang === 'fr' ? 'Ctrl+Alt+' : 'Strg+Alt+') : '⌃⌥') + TRAY_ACTIONS[app].letter;

/** The design system's Avatar (bundle components/core/Avatar.jsx). */
const Avatar = ({ initials, color = '#1375BD', size = 28 }: { initials: string; color?: string; size?: number }) => (
  <span title={initials} style={{ width: size, height: size, borderRadius: '50%', background: color, border: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', font: `700 ${Math.round(size * 0.37)}px/1 Barlow, ui-sans-serif, system-ui, sans-serif`, letterSpacing: '.04em', color: '#FBFAFA', flex: 'none' }}>{initials}</span>
);
/** The design system's Switch (bundle components/forms/Switch.jsx). */
const Switch = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
  <label onClick={(e) => { e.stopPropagation(); onChange(!checked); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', font: '600 13px/1 Inter, sans-serif', color: '#FBFAFA' }}>
    <span style={{ width: 30, height: 17, borderRadius: 9, background: checked ? '#05A79D' : '#4A4546', position: 'relative', flex: 'none', transition: 'background 200ms' }}>
      <span style={{ position: 'absolute', top: 2, left: checked ? 15 : 2, width: 13, height: 13, borderRadius: '50%', background: '#FBFAFA', transition: 'left 200ms cubic-bezier(.2,.7,.2,1)' }} />
    </span>
  </label>
);
const Row = ({ children, onClick, style }: { children: ReactNode; onClick?: () => void; style?: CSSProperties }) => (
  <div className="rtm-row" onClick={onClick} style={style}>{children}</div>
);

export function TrayMenu(p: TrayMenuProps) {
  const lang: TrayLang = p.lang === 'fr' ? 'fr' : 'de', t = T[lang], app = TRAY_ACTIONS[p.app], win = p.platform === 'win', online = p.online ?? true;
  const [focus, setFocus] = useState(-1), root = useRef<HTMLDivElement>(null);
  useEffect(() => { root.current?.focus(); }, []);
  const payOn = !!p.payOn, setPay = (v: boolean) => p.onPayChange?.(v);
  const mod = win ? (lang === 'fr' ? 'Ctrl+' : 'Strg+') : '⌘';
  const actions = app.actions.map((a, i) => {
    // Off like the design's offline state: offline, or listed in `disabled` (with its tooltip).
    const why = p.disabled?.[a.id], off = !online || why !== undefined;
    return {
      ...a, label: a[lang], shortcut: a.key ? mod + a.key : '', focused: focus === i, off, why,
      color: off ? '#5F5A5C' : '#FBFAFA', iconColor: off ? '#5F5A5C' : '#9B9698',
      run: () => { if (off) return; if (a.sw) setPay(!payOn); else p.onAction(a.id); },
    };
  });
  // Arrow keys skip the entries that are off (all off → nothing to focus).
  const step = (from: number, d: 1 | -1) => {
    const n = actions.length;
    for (let k = 1; k <= n; k++) { const j = (((from < 0 && d < 0 ? n : from) + d * k) % n + n) % n; if (!actions[j]!.off) return j; }
    return -1;
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocus(step(focus, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocus(step(focus < 0 ? actions.length : focus, -1)); }
    else if (e.key === 'Enter' && actions[focus]) { e.preventDefault(); actions[focus]!.run(); }
    else if (e.key === 'Escape') { if (focus >= 0) setFocus(-1); else p.onAction('close'); }
  };
  const connText = online ? t.online + (p.host || '–') : t.offline;
  return (
    <div ref={root} className="rtm" tabIndex={0} onKeyDown={onKey}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <img src={p.mark} alt="" style={{ height: 22, display: 'block' }} />
          <span style={{ fontFamily: 'Barlow,sans-serif', fontSize: 19, lineHeight: 1, letterSpacing: '-.01em', whiteSpace: 'nowrap' }}><span style={{ fontWeight: 800, color: '#FBFAFA' }}>Rimo</span><span style={{ fontWeight: 600, color: '#9B9698' }}>{app.name.replace(/^Rimo/, '')}</span></span>
        </div>
        <span title={connText} style={{ width: 8, height: 8, borderRadius: '50%', background: online ? '#05A79D' : '#E0A340', flex: 'none' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px 14px' }}>
        <Avatar initials={p.user.initials} color={p.user.color} size={28} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: '#CDD3D9', lineHeight: 1.3 }}>{t.signedIn} <span style={{ color: '#FBFAFA', fontWeight: 600 }}>{p.user.name}</span></div>
          <div style={{ fontSize: 12, lineHeight: 1.3, color: online ? '#9B9698' : '#E0A340' }}>{connText}</div>
        </div>
      </div>
      <div className="rtm-sep" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 6 }}>
        {actions.map((a, i) => (
          <div key={a.id} className="rtm-action" onClick={a.run} onMouseEnter={() => setFocus(-1)} data-index={i} title={a.why} aria-disabled={a.off || undefined}>
            {a.focused && <div style={{ position: 'absolute', inset: 0, borderRadius: 4, background: 'rgba(19,117,189,.14)', boxShadow: 'inset 0 0 0 2px #1375BD', pointerEvents: 'none' }} />}
            <span style={{ display: 'flex', color: a.iconColor, position: 'relative' }}>{SVG(ICONS[a.icon]!)}</span>
            <span style={{ flex: 1, fontSize: 13, position: 'relative', color: a.color }}>{a.label}</span>
            {a.sw && <span style={{ position: 'relative', display: 'flex' }}><Switch checked={payOn} onChange={setPay} /></span>}
            {a.shortcut && <span style={{ position: 'relative', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#8C8789' }}>{a.shortcut}</span>}
          </div>
        ))}
        {!online && <div style={{ padding: '6px 10px 4px', fontSize: 12, lineHeight: 1.4, color: '#9B9698', textWrap: 'pretty' } as CSSProperties}>{t.offlineHint}</div>}
      </div>
      <div className="rtm-sep" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 16px 12px' }}>
        <div style={{ fontFamily: 'Barlow,sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: '#9B9698' }}>{t.programs}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,minmax(0,1fr))', gap: 4 }}>
          {p.suite.map((s) => {
            const current = s.key === p.app, run = current || !!s.running;
            return (
              <div key={s.key} className="rtm-hex" title={run ? `${s.name} · ${t.runningHere}` : s.name} onClick={() => !current && p.onSuite(s.key)}>
                {current && <div style={{ position: 'absolute', inset: 0, borderRadius: 6, background: 'rgba(19,117,189,.14)', boxShadow: 'inset 0 0 0 1px rgba(19,117,189,.45)' }} />}
                <img src={s.src} alt={s.name} style={{ width: 26, height: 26, display: 'block', position: 'relative' }} />
                {run && <span style={{ position: 'absolute', right: 5, bottom: 5, width: 7, height: 7, borderRadius: '50%', background: '#05A79D', boxShadow: '0 0 0 2px #231F20' }} />}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#8C8789' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#05A79D' }} />
          <span>{t.runningHere}</span>
        </div>
      </div>
      <div className="rtm-sep" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 6 }}>
        <Row onClick={p.onShowWindow}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9B9698" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M10 4v4" /><path d="M2 8h20" /><path d="M6 4v4" /></svg>
          <span style={{ flex: 1, fontSize: 13, color: '#CDD3D9' }}>{t.showWindow}</span>
        </Row>
        <Row onClick={p.onQuit}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9B9698" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v10" /><path d="M18.4 6.6a9 9 0 1 1-12.77.04" /></svg>
          <span style={{ flex: 1, fontSize: 13, color: '#CDD3D9' }}>{t.quit}</span>
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#8C8789' }}>{win ? (lang === 'fr' ? 'Ctrl+Q' : 'Strg+Q') : '⌘Q'}</span>
        </Row>
        {p.globalShortcut && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '6px 10px 4px', fontSize: 11, color: '#8C8789' }}>
            <span>{t.globalKey}</span>
            <span style={{ fontFamily: "'JetBrains Mono',monospace" }}>{trayGlobalKey(p.app, win ? 'win' : 'mac', lang)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
